import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { KeyRound, Trash2 } from 'lucide-react';
import { api, throwIfNotOk } from '../../lib/api';
import { qk } from '../../lib/queryKeys';
import { Card } from '@siliconx/shared/ui/Card';
import { PanelTitle } from '@siliconx/shared/ui/PanelTitle';
import { Button } from '@siliconx/shared/ui/Button';

/**
 * SIL-585 (Wave 4/UI, spec §8/§11) — BMC credential store settings card.
 * Per-endpoint entry (endpoint URL + a dedicated read-only Redfish account).
 * WRITE-ONLY: the secret value is entered here and never shown again — GET
 * only ever returns endpoint_url + probe metadata, never anything
 * credential-shaped. A fleet may re-paste the same account into multiple
 * entries (each still gets its own Secrets Manager secret server-side).
 */

interface AssetOpt {
  id: string;
  serialNumber: string | null;
  manufacturerName: string | null;
  modelName: string | null;
}

interface BmcEndpoint {
  assetId: string;
  endpointUrl: string;
  tier: number | null;
  readonlyAssertion: boolean | null;
  probedAt: string | null;
  createdAt: string;
  serialNumber: string | null;
}

const UNPROBED_BADGE = { label: 'Not yet probed', className: 'bg-muted text-muted-foreground' };

function tierBadge(probedAt: string | null, tier: number | null): { label: string; className: string } {
  if (!probedAt) return UNPROBED_BADGE;
  if (tier === 1) return { label: 'Tier 1', className: 'bg-emerald-100 text-emerald-700' };
  if (tier === 2) return { label: 'Tier 2', className: 'bg-amber-100 text-amber-800' };
  if (tier === 3) return { label: 'Tier 3', className: 'bg-muted text-muted-foreground' };
  return UNPROBED_BADGE;
}

function assetLabel(a: { serialNumber: string | null; manufacturerName?: string | null; modelName?: string | null }) {
  const model = [a.manufacturerName, a.modelName].filter(Boolean).join(' ');
  return a.serialNumber ? `${a.serialNumber}${model ? ` — ${model}` : ''}` : (model || 'Unnamed asset');
}

function RegisterForm({ assets, onRegistered }: { assets: AssetOpt[]; onRegistered: () => void }) {
  const [assetId, setAssetId] = useState('');
  const [endpointUrl, setEndpointUrl] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [err, setErr] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: async () => {
      const res = await api.api.client['bmc-endpoints'].$post({
        json: { assetId, endpointUrl: endpointUrl.trim(), username: username.trim(), password },
      });
      await throwIfNotOk(res);
      return res.json();
    },
    onSuccess: () => {
      setAssetId('');
      setEndpointUrl('');
      setUsername('');
      setPassword('');
      setErr(null);
      onRegistered();
    },
    onError: (e: unknown) => setErr((e as Error).message),
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        mutation.mutate();
      }}
      className="space-y-2 rounded-md border border-border p-3"
    >
      <label className="block text-xs font-medium text-muted-foreground">
        Asset
        <select
          required
          value={assetId}
          onChange={(e) => setAssetId(e.target.value)}
          className="mt-1 w-full rounded-md border border-border px-2 py-1 text-xs"
        >
          <option value="">Select an asset…</option>
          {assets.map((a) => (
            <option key={a.id} value={a.id}>
              {assetLabel(a)}
            </option>
          ))}
        </select>
      </label>
      <label className="block text-xs font-medium text-muted-foreground">
        BMC endpoint URL
        <input
          required
          type="url"
          value={endpointUrl}
          onChange={(e) => setEndpointUrl(e.target.value)}
          placeholder="https://10.x.x.x/redfish/v1"
          className="mt-1 w-full rounded-md border border-border px-2 py-1 font-mono text-xs"
        />
      </label>
      <label className="block text-xs font-medium text-muted-foreground">
        Read-only account username
        <input
          required
          type="text"
          autoComplete="off"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          className="mt-1 w-full rounded-md border border-border px-2 py-1 text-xs"
        />
      </label>
      <label className="block text-xs font-medium text-muted-foreground">
        Password
        <input
          required
          type="password"
          autoComplete="off"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="mt-1 w-full rounded-md border border-border px-2 py-1 text-xs"
        />
      </label>
      <p className="text-xs text-muted-foreground">
        Use a dedicated, read-only Redfish account for this endpoint — SiliconX refuses to run a
        hardware-attested test against an account with write privileges. The password is stored
        encrypted and is never shown again; re-registering an endpoint replaces it.
      </p>
      {err && <div className="text-xs text-red-600">{err}</div>}
      <Button size="sm" type="submit" disabled={mutation.isPending || !assetId}>
        {mutation.isPending ? 'Saving…' : 'Save credential'}
      </Button>
    </form>
  );
}

export function BmcCredentialsCard({ isAdmin }: { isAdmin: boolean }) {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);

  const { data: endpoints, isLoading } = useQuery<{ data: BmcEndpoint[] }>({
    queryKey: qk.bmcEndpoints(),
    queryFn: async () => {
      const res = await api.api.client['bmc-endpoints'].$get();
      await throwIfNotOk(res);
      return (await res.json()) as unknown as { data: BmcEndpoint[] };
    },
  });

  const { data: assetsResp } = useQuery<{ data: AssetOpt[] }>({
    queryKey: ['client-assets-for-bmc-picker'],
    queryFn: async () => {
      const res = await api.api.client.assets.$get({ query: { limit: '200' } });
      await throwIfNotOk(res);
      return (await res.json()) as unknown as { data: AssetOpt[] };
    },
    enabled: showForm,
  });

  const remove = useMutation({
    mutationFn: async (assetId: string) => {
      const res = await api.api.client['bmc-endpoints'][':assetId'].$delete({ param: { assetId } });
      await throwIfNotOk(res);
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: qk.bmcEndpoints() }),
  });

  const rows = endpoints?.data ?? [];

  return (
    <Card className="space-y-3 md:col-span-2 lg:col-span-3">
      <div className="flex items-center gap-2">
        <KeyRound className="size-5 shrink-0 text-muted-foreground" />
        <PanelTitle
          title="BMC credentials"
          description="Register each asset's BMC (Redfish) endpoint with a dedicated read-only account so SiliconX can run hardware-attested Active Verification tests. Write-only — a stored password is never shown again."
          className="min-w-0 flex-1"
        />
      </div>

      {isLoading ? (
        <p className="text-xs text-muted-foreground">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="text-xs text-muted-foreground">No BMC endpoints registered yet.</p>
      ) : (
        <div className="space-y-1.5">
          {rows.map((r) => {
            const badge = tierBadge(r.probedAt, r.tier);
            return (
              <div
                key={r.assetId}
                className="flex items-center justify-between gap-2 rounded-md border border-border px-3 py-2"
              >
                <div className="min-w-0">
                  <div className="truncate text-xs font-medium text-foreground">
                    {r.serialNumber ?? r.assetId.slice(0, 8)}
                  </div>
                  <div className="truncate font-mono text-[13px] text-muted-foreground">{r.endpointUrl}</div>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${badge.className}`}>
                    {badge.label}
                  </span>
                  {isAdmin && (
                    <button
                      type="button"
                      onClick={() => {
                        if (confirm('Remove this BMC endpoint and delete its stored credential?')) {
                          remove.mutate(r.assetId);
                        }
                      }}
                      className="text-muted-foreground hover:text-red-600"
                      aria-label="Remove BMC endpoint"
                    >
                      <Trash2 className="size-3.5" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {isAdmin ? (
        showForm ? (
          <RegisterForm
            assets={assetsResp?.data ?? []}
            onRegistered={() => {
              setShowForm(false);
              void queryClient.invalidateQueries({ queryKey: qk.bmcEndpoints() });
            }}
          />
        ) : (
          <Button size="sm" onClick={() => setShowForm(true)}>
            Add BMC endpoint
          </Button>
        )
      ) : (
        <p className="text-xs text-muted-foreground">Only an org admin can manage BMC credentials.</p>
      )}
    </Card>
  );
}
